import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../auth/authSlice';
import { useGetValoresUnicosQuery } from '../api/clientesApiSlice';

interface ClienteImportModalProps {
  onClose: () => void;
}

// Campos de la base de datos para el mapeo
const DB_FIELDS = [
  { key: 'documento', label: 'Documento' },
  { key: 'nombre', label: 'Nombre' },
  { key: 'apellido', label: 'Apellido' },
  { key: 'telefono', label: 'Teléfono' },
  { key: 'direccion', label: 'Dirección' },
  { key: 'localidad', label: 'Localidad' },
  { key: 'provincia', label: 'Provincia' },
  { key: 'pais', label: 'País' },
  { key: 'empresa_id', label: 'Empresa ID' },
  { key: 'texto1', label: 'Texto 1' },
  { key: 'texto2', label: 'Texto 2' },
  { key: 'texto3', label: 'Texto 3' },
  { key: 'texto4', label: 'Texto 4' },
  { key: 'texto5', label: 'Texto 5' },
];

const API_URL = import.meta.env.VITE_API_URL || '';

const ClienteImportModal: React.FC<ClienteImportModalProps> = ({ onClose }) => {
  const currentUser = useSelector(selectCurrentUser);
  const empresaId = currentUser?.empresa_id;
  const empresaNombre = currentUser?.empresa?.nombre || '';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<string[][]>([]);
  const [allRows, setAllRows] = useState<string[][]>([]);
  const [error, setError] = useState('');
  const [delimiter, setDelimiter] = useState(',');
  const [quoteChar, setQuoteChar] = useState('"');
  const [mapping, setMapping] = useState<{ [col: number]: string }>({});
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ success: number; failed: number; errors: string[] }>({ success: 0, failed: 0, errors: [] });
  const { data: valoresUnicos, isLoading: loadingValoresUnicos } = useGetValoresUnicosQuery(
    { empresa_id: empresaId },
    { skip: !empresaId }
  );
  const [manualFields, setManualFields] = useState<{ [campo: string]: string }>({});
  const [newFieldValues, setNewFieldValues] = useState<{ [campo: string]: string }>({});
  const isMapped = (campo: string) => Object.values(mapping).includes(campo);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError('');
    setImportResult({ success: 0, failed: 0, errors: [] });
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      // Parse CSV simple (no robusto, pero respeta delimitador y comillas)
      const rows = text.split(/\r?\n/).filter(Boolean).map(row => {
        // Separar por delimitador, respetando comillas
        const regex = new RegExp(`${quoteChar}([^${quoteChar}]*)${quoteChar}|[^${delimiter}]+`, 'g');
        const matches = row.match(regex);
        if (matches) {
          return matches.map(cell => cell.replace(new RegExp(`^${quoteChar}|${quoteChar}$`, 'g'), ''));
        }
        return row.split(delimiter);
      });
      setAllRows(rows);
      setPreview(rows.slice(0, 10));
      setMapping({});
    };
    reader.onerror = () => setError('No se pudo leer el archivo.');
    reader.readAsText(file);
  };

  const handleMappingChange = (colIdx: number, dbField: string) => {
    setMapping((prev) => ({ ...prev, [colIdx]: dbField }));
  };

  // Validar que nombre y apellido estén mapeados
  const requiredFields = ['nombre', 'apellido'];
  const missingRequired = requiredFields.filter(req => !Object.values(mapping).includes(req));

  // Importar clientes
  const handleImport = async () => {
    setError('');
    setImportResult({ success: 0, failed: 0, errors: [] });
    if (!empresaId) {
      setError('No tienes una empresa asignada. Contacta a un administrador.');
      return;
    }
    if (missingRequired.length > 0) {
      setError('Debes mapear los campos requeridos: ' + missingRequired.map(f => DB_FIELDS.find(df => df.key === f)?.label).join(', '));
      return;
    }
    if (!allRows.length) {
      setError('No hay datos para importar.');
      return;
    }
    setImporting(true);
    let success = 0;
    let failed = 0;
    const errors: string[] = [];
    // Recorrer filas (excepto cabecera)
    for (let i = 1; i < allRows.length; i++) {
      const row = allRows[i];
      // Construir objeto cliente según mapeo
      const cliente: Record<string, string | number> = { empresa_id: empresaId };
      Object.entries(mapping).forEach(([colIdx, dbField]) => {
        cliente[dbField] = row[Number(colIdx)] ?? '';
      });
      // Asignar valores manuales si corresponde
      ['localidad', 'provincia', 'pais'].forEach(campo => {
        if (!isMapped(campo)) {
          if (manualFields[campo] === '__nuevo__') {
            cliente[campo] = newFieldValues[campo] || '';
          } else if (manualFields[campo]) {
            cliente[campo] = manualFields[campo];
          }
        }
      });
      // Limpiar: solo enviar campos con valor
      Object.keys(cliente).forEach((key) => {
        if (cliente[key] === undefined || cliente[key] === '') {
          delete cliente[key];
        }
      });
      // Validar requeridos y campos manuales
      for (const campo of ['localidad', 'provincia', 'pais']) {
        if (!isMapped(campo) && !manualFields[campo]) {
          setError(`Debes asignar un valor para el campo ${campo}.`);
          setImporting(false);
          return;
        }
        // Validar que si eligió "Agregar nuevo..." realmente ingresó un valor
        if (!isMapped(campo) && manualFields[campo] === '__nuevo__' && !newFieldValues[campo]) {
          setError(`Debes ingresar un valor para el campo ${campo}.`);
          setImporting(false);
          return;
        }
      }
      // Validar requeridos
      if (!cliente.nombre || !cliente.apellido || !cliente.empresa_id) {
        failed++;
        errors.push(`Fila ${i + 1}: Faltan campos obligatorios.`);
        continue;
      }
      try {
        const res = await fetch(`${API_URL}/clientes/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(cliente),
        });
        if (res.ok) {
          success++;
        } else {
          const data = await res.json();
          failed++;
          errors.push(`Fila ${i + 1}: ${data.message || 'Error desconocido.'}`);
        }
      } catch {
        failed++;
        errors.push(`Fila ${i + 1}: Error de red.`);
      }
    }
    setImportResult({ success, failed, errors });
    setImporting(false);
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 text-foreground dark:text-white">Importar clientes</h2>
      {/* Mostrar empresa seleccionada */}
      <div className="mb-4">
        <span className="block text-sm font-semibold text-muted-foreground">Empresa seleccionada:</span>
        {empresaId ? (
          <span className="block text-base font-bold text-foreground">{empresaNombre} (ID: {empresaId})</span>
        ) : (
          <span className="block text-destructive text-sm">No tienes una empresa asignada. Contacta a un administrador.</span>
        )}
      </div>
      <div className="mb-4 flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <input
            type="file"
            accept=".csv"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
          />
          {fileName && <div className="mt-2 text-xs text-muted-foreground">Archivo seleccionado: <span className="font-semibold">{fileName}</span></div>}
          {error && <div className="mt-2 text-destructive">{error}</div>}
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-xs font-semibold text-muted-foreground">Delimitador de campo</label>
          <select
            className="rounded-md border px-2 py-1 bg-background border-border text-foreground"
            value={delimiter}
            onChange={e => setDelimiter(e.target.value)}
          >
            <option value=",">Coma (,)</option>
            <option value=";">Punto y coma (;)</option>
            <option value="\t">Tabulación (Tab)</option>
            <option value="|">Barra vertical (|)</option>
          </select>
          <label className="text-xs font-semibold text-muted-foreground mt-2">Separador de texto (comillas)</label>
          <select
            className="rounded-md border px-2 py-1 bg-background border-border text-foreground"
            value={quoteChar}
            onChange={e => setQuoteChar(e.target.value)}
          >
            <option value='"'>Comillas dobles (")</option>
            <option value="'">Comillas simples (')</option>
          </select>
        </div>
      </div>
      {preview.length > 0 && (
        <div className="mb-4">
          <div className="font-semibold mb-2 text-muted-foreground">Vista previa (primeras 10 filas):</div>
          {missingRequired.length > 0 && (
            <div className="mb-2 text-destructive text-xs">
              Debes mapear los campos requeridos: {missingRequired.map(f => DB_FIELDS.find(df => df.key === f)?.label).join(', ')}
            </div>
          )}
          {/* Campos manuales para localidad, provincia y país */}
          {['localidad', 'provincia', 'pais'].map((campo) =>
            !isMapped(campo) && (
              <div key={campo} className="mb-2">
                <label className="block text-xs font-semibold mb-1 text-muted-foreground">
                  Asignar {campo} a todos los clientes importados:
                </label>
                <select
                  className="rounded-md border px-2 py-1 bg-background border-border text-foreground"
                  value={manualFields[campo] || ''}
                  onChange={e => setManualFields({ ...manualFields, [campo]: e.target.value })}
                >
                  <option value="">-- Seleccionar --</option>
                  {valoresUnicos?.[campo]?.map(val => (
                    <option key={val} value={val}>{val}</option>
                  ))}
                  <option value="__nuevo__">Agregar nuevo...</option>
                </select>
                {manualFields[campo] === '__nuevo__' && (
                  <input
                    type="text"
                    className="mt-1 rounded-md border px-2 py-1 w-full"
                    placeholder={`Ingrese ${campo}`}
                    value={newFieldValues[campo] || ''}
                    onChange={e => setNewFieldValues({ ...newFieldValues, [campo]: e.target.value })}
                  />
                )}
              </div>
            )
          )}
          <div className="overflow-x-auto rounded border border-border bg-muted/30">
            <table className="min-w-full text-xs">
              <thead>
                <tr>
                  {preview[0].map((col, idx) => (
                    <th key={idx} className="px-2 py-1 border-b border-border bg-muted/50 text-foreground font-semibold">
                      {col}
                      <div className="mt-1">
                        <select
                          className="rounded border px-1 py-0.5 bg-background border-border text-xs text-foreground"
                          value={mapping[idx] || ''}
                          onChange={e => handleMappingChange(idx, e.target.value)}
                          disabled={importing}
                        >
                          <option value="">-- Mapear --</option>
                          {DB_FIELDS.map(f => (
                            <option key={f.key} value={f.key}>{f.label}</option>
                          ))}
                        </select>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.slice(1).map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td key={j} className="px-2 py-1 border-b border-border text-foreground">{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2 mt-6">
        <button
          className="bg-muted text-foreground px-4 py-2 rounded-md font-semibold hover:bg-accent transition"
          onClick={onClose}
          disabled={importing}
        >
          Cancelar
        </button>
        <button
          className="bg-primary text-primary-foreground px-4 py-2 rounded-md font-semibold hover:bg-primary/90 transition disabled:opacity-50"
          onClick={handleImport}
          disabled={importing || !empresaId || missingRequired.length > 0 || !allRows.length}
        >
          {importing ? 'Importando...' : 'Importar'}
        </button>
      </div>
      {importResult.success > 0 && (
        <div className="mt-4 text-success">{importResult.success} clientes importados correctamente.</div>
      )}
      {importResult.failed > 0 && (
        <div className="mt-4 text-destructive">
          {importResult.failed} clientes no se pudieron importar.
          <ul className="list-disc ml-6 mt-2 text-xs">
            {importResult.errors.map((err, idx) => <li key={idx}>{err}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ClienteImportModal; 